/**
 * Posts List - Display and manage social media posts
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  Input,
  Select,
  Spinner,
  Text,
  useToast,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  InputGroup,
  InputLeftElement,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import {
  getAllPosts,
  deletePost,
  updatePost,
  duplicatePost,
  SocialMediaPost,
  PostStatus,
  SocialPlatform,
} from '../../services/socialMediaService';

interface PostsListProps {
  onEdit: (postId: string) => void;
  onDataChange: () => void; // Callback to refresh parent data (e.g., stats)
}

const STATUS_COLORS: Record<PostStatus, string> = {
  draft: 'gray',
  scheduled: 'blue',
  published: 'green',
  failed: 'red',
};

const PLATFORM_COLORS: Record<SocialPlatform, string> = {
  facebook: 'facebook',
  instagram: 'pink',
  linkedin: 'linkedin',
  tiktok: 'purple',
};

const PLATFORM_ICONS: Record<SocialPlatform, string> = {
  facebook: '📘',
  instagram: '📷',
  linkedin: '💼',
  tiktok: '🎵',
};

export default function PostsList({ onEdit, onDataChange }: PostsListProps) {
  const [posts, setPosts] = useState<SocialMediaPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<PostStatus | ''>('');
  const [filterPlatform, setFilterPlatform] = useState<SocialPlatform | ''>('');
  const [sortField, setSortField] = useState<keyof SocialMediaPost>('createdAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [publishingPostId, setPublishingPostId] = useState<string | null>(null);
  const toast = useToast();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const [postToDelete, setPostToDelete] = useState<string | null>(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const fetchedPosts = await getAllPosts(
        {
          status: filterStatus || undefined,
          platform: filterPlatform || undefined,
          searchTerm: searchTerm || undefined,
        },
        sortField,
        sortDirection
      );
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load posts.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPlatform, searchTerm, sortField, sortDirection, toast]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleDeleteClick = (postId: string) => {
    setPostToDelete(postId);
    onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (postToDelete) {
      try {
        await deletePost(postToDelete);
        toast({
          title: 'Post deleted',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPosts();
        onDataChange();
      } catch (error) {
        console.error('Failed to delete post:', error);
        toast({
          title: 'Error',
          description: 'Failed to delete post.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        onClose();
        setPostToDelete(null);
      }
    }
  };

  const handleDuplicate = async (postId: string) => {
    try {
      const newId = await duplicatePost(postId);
      if (newId) {
        toast({
          title: 'Post duplicated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchPosts();
        onDataChange();
      }
    } catch (error) {
      console.error('Failed to duplicate post:', error);
      toast({
        title: 'Error',
        description: 'Failed to duplicate post.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleStatusChange = async (postId: string, newStatus: PostStatus) => {
    try {
      await updatePost(postId, { status: newStatus });
      toast({
        title: `Status updated to ${newStatus}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      fetchPosts();
      onDataChange();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePublish = async (postId: string) => {
    try {
      setPublishingPostId(postId);
      
      // Get Firebase Functions instance
      const app = getApp();
      const functions = getFunctions(app);
      const publishPost = httpsCallable(functions, 'publishPost');

      // Call the cloud function
      const result = await publishPost({ postId });
      const data = result.data as { success: boolean; results: Record<string, { success: boolean; error?: string }> };

      if (data.success) {
        toast({
          title: '🎉 Published successfully!',
          description: 'Your post is now live on selected platforms.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // Some platforms succeeded, some failed
        const failedPlatforms = Object.entries(data.results)
          .filter(([_, result]) => !result.success)
          .map(([platform, result]) => `${platform}: ${result.error}`)
          .join(', ');

        toast({
          title: '⚠️ Partial success',
          description: `Some platforms failed: ${failedPlatforms}`,
          status: 'warning',
          duration: 7000,
          isClosable: true,
        });
      }

      fetchPosts();
      onDataChange();
    } catch (error: unknown) {
      console.error('Failed to publish post:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      toast({
        title: '❌ Publish failed',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setPublishingPostId(null);
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box bg="white" p={6} borderRadius="lg" boxShadow="sm" borderWidth="1px">
      <Flex mb={4} wrap="wrap" gap={4}>
        <InputGroup maxW="300px">
          <InputLeftElement pointerEvents="none">
            🔍
          </InputLeftElement>
          <Input
            placeholder="Search posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Select
          placeholder="Filter by Status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as PostStatus | '')}
          maxW="150px"
        >
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="published">Published</option>
          <option value="failed">Failed</option>
        </Select>

        <Select
          placeholder="Filter by Platform"
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value as SocialPlatform | '')}
          maxW="150px"
        >
          <option value="facebook">Facebook</option>
          <option value="instagram">Instagram</option>
          <option value="linkedin">LinkedIn</option>
          <option value="tiktok">TikTok</option>
        </Select>

        <Select
          value={`${sortField}:${sortDirection}`}
          onChange={(e) => {
            const [field, direction] = e.target.value.split(':');
            setSortField(field as keyof SocialMediaPost);
            setSortDirection(direction as 'asc' | 'desc');
          }}
          maxW="200px"
        >
          <option value="createdAt:desc">Newest First</option>
          <option value="createdAt:asc">Oldest First</option>
          <option value="scheduledFor:asc">Scheduled (Soonest)</option>
          <option value="publishedAt:desc">Recently Published</option>
        </Select>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" height="200px">
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : posts.length === 0 ? (
        <Text textAlign="center" mt={8} fontSize="lg" color="gray.500">
          No posts found. Create your first social media post!
        </Text>
      ) : (
        <Table variant="simple" mt={4}>
          <Thead>
            <Tr>
              <Th>Content</Th>
              <Th>Platforms</Th>
              <Th>Status</Th>
              <Th>Schedule/Published</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {posts.map((post) => (
              <Tr key={post.id}>
                <Td maxW="300px">
                  <Text fontWeight="medium" mb={1}>
                    {truncateText(post.content, 100)}
                  </Text>
                  {post.hashtags.length > 0 && (
                    <Text fontSize="sm" color="gray.500">
                      {post.hashtags.slice(0, 3).map(tag => `#${tag}`).join(' ')}
                      {post.hashtags.length > 3 && ` +${post.hashtags.length - 3} more`}
                    </Text>
                  )}
                </Td>
                <Td>
                  <HStack spacing={1}>
                    {post.platforms.map(platform => (
                      <Tooltip key={platform} label={platform}>
                        <Badge colorScheme={PLATFORM_COLORS[platform]} fontSize="lg">
                          {PLATFORM_ICONS[platform]}
                        </Badge>
                      </Tooltip>
                    ))}
                  </HStack>
                </Td>
                <Td>
                  <Badge colorScheme={STATUS_COLORS[post.status]}>
                    {post.status}
                  </Badge>
                </Td>
                <Td>
                  {post.status === 'scheduled' && post.scheduledFor ? (
                    <Text fontSize="sm">
                      📅 {format(post.scheduledFor.toDate(), 'MMM dd, yyyy h:mm a')}
                    </Text>
                  ) : post.status === 'published' && post.publishedAt ? (
                    <Text fontSize="sm">
                      ✅ {format(post.publishedAt.toDate(), 'MMM dd, yyyy')}
                    </Text>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      —
                    </Text>
                  )}
                </Td>
                <Td>
                  <Flex gap={2}>
                    {(post.status === 'draft' || post.status === 'scheduled') && (
                      <Button
                        size="sm"
                        colorScheme="green"
                        isLoading={publishingPostId === post.id}
                        loadingText="Publishing..."
                        onClick={() => handlePublish(post.id!)}
                      >
                        🚀 Publish
                      </Button>
                    )}
                    <Button size="sm" onClick={() => onEdit(post.id!)}>
                      Edit
                    </Button>
                    <Menu>
                      <MenuButton as={Button} size="sm" variant="ghost">
                        •••
                      </MenuButton>
                      <MenuList>
                        {post.status === 'draft' && (
                          <MenuItem onClick={() => handleStatusChange(post.id!, 'scheduled')}>
                            📅 Mark as Scheduled
                          </MenuItem>
                        )}
                        {(post.status === 'draft' || post.status === 'scheduled') && (
                          <MenuItem onClick={() => handleStatusChange(post.id!, 'published')}>
                            ✅ Mark as Published (Manual)
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => handleDuplicate(post.id!)}>
                          📋 Duplicate
                        </MenuItem>
                        <MenuItem color="red.500" onClick={() => handleDeleteClick(post.id!)}>
                          🗑️ Delete
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Post
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure? You can't undo this action.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
